import { Controller } from "@hotwired/stimulus"
import Fuse from "fuse.js"

// Public Help Center search (HC-2, #261).
//
// Lazy-loads the search index (cached JSON, 1h TTL server-side) on first
// keystroke and runs a Fuse fuzzy match against title / body excerpt /
// tags. Results render inline below the input; clicking a result deep
// links to the topic page.
//
// Why client-side: the public catalog is small (low double-digit topics
// at launch). Server-side FTS lands in HC-6 (#265) when scale demands it.
// Fuse adds ~12KB gzipped to the bundle vs. shipping a search service.
export default class extends Controller {
  static targets = ["input", "results"]
  static values = {
    endpoint: String,
    debounce: { type: Number, default: 200 },
    minChars: { type: Number, default: 2 },
    limit: { type: Number, default: 10 }
  }

  connect() {
    this.fuse = null
    this.indexPromise = null
    this.debounceTimer = null
  }

  disconnect() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }
  }

  search(event) {
    const query = (event.target.value || "").trim()

    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this._runSearch(query), this.debounceValue)
  }

  async _runSearch(query) {
    if (query.length < this.minCharsValue) {
      this._hideResults()
      return
    }

    try {
      await this._ensureIndex()
    } catch (err) {
      console.error("[help-search] failed to load index", err)
      this._renderError()
      return
    }

    const matches = this.fuse.search(query, { limit: this.limitValue })
    this._renderResults(matches.map((m) => m.item), query)
  }

  async _ensureIndex() {
    if (this.fuse) return
    if (!this.indexPromise) {
      this.indexPromise = fetch(this.endpointValue, {
        headers: { Accept: "application/json" },
        credentials: "same-origin"
      }).then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json()
      })
    }
    const data = await this.indexPromise
    this.fuse = new Fuse(data, {
      keys: [
        { name: "title", weight: 0.6 },
        { name: "tags", weight: 0.25 },
        { name: "body_excerpt", weight: 0.15 }
      ],
      threshold: 0.4,
      ignoreLocation: true,
      includeScore: false,
      minMatchCharLength: 2
    })
  }

  _renderResults(items, query) {
    if (!this.hasResultsTarget) return
    if (items.length === 0) {
      this.resultsTarget.innerHTML =
        `<p class="help-center__search-empty">${this._safeText(this._t("no_results"))}</p>`
      this.resultsTarget.hidden = false
      return
    }

    const html = items.map((item) => this._renderItem(item, query)).join("")
    this.resultsTarget.innerHTML = `<ul class="help-center__search-list">${html}</ul>`
    this.resultsTarget.hidden = false
  }

  _renderItem(item, query) {
    const href = `/help-center/${encodeURIComponent(item.category_slug)}/${encodeURIComponent(item.slug)}`
    const tags = (item.tags || []).slice(0, 3)
      .map((t) => `<span class="help-center__tag-chip">${this._safeText(t)}</span>`)
      .join("")
    return `
      <li class="help-center__search-item">
        <a href="${href}" class="help-center__search-link">
          <span class="help-center__search-title">${this._safeText(item.title)}</span>
          <span class="help-center__search-excerpt">${this._safeText(item.body_excerpt || "")}</span>
          ${tags ? `<span class="help-center__search-tags">${tags}</span>` : ""}
        </a>
      </li>
    `
  }

  _renderError() {
    if (!this.hasResultsTarget) return
    this.resultsTarget.innerHTML =
      `<p class="help-center__search-empty">${this._safeText(this._t("error"))}</p>`
    this.resultsTarget.hidden = false
  }

  _hideResults() {
    if (!this.hasResultsTarget) return
    this.resultsTarget.innerHTML = ""
    this.resultsTarget.hidden = true
  }

  // Tiny escape so user input rendered via innerHTML stays inert. Title /
  // body / tags originate from authored Help Topic records; even though
  // they're admin-curated we don't want a stray "<" to ever execute.
  _safeText(value) {
    return String(value || "").replace(/[&<>"']/g, (c) => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      "\"": "&quot;",
      "'": "&#39;"
    })[c])
  }

  _t(key) {
    const map = {
      no_results: document.documentElement.lang === "en" ? "No results found." : "Nenhum resultado encontrado.",
      error: document.documentElement.lang === "en" ? "Could not load search." : "Não foi possível carregar a busca."
    }
    return map[key]
  }
}
